import React from 'react';
import { ActivityIcon, CheckIcon, GraduationCapIcon, PhoneIcon, RouteIcon, UsersRoundIcon } from 'lucide-react';
import { ARCHIVE_CONTACT, ARCHIVE_SHOCKWAVE_IMAGE, archivePartnerBrands } from './archiveData';
const REVIEW_FACTORS = [{
  label: 'Patient Needs',
  icon: UsersRoundIcon
}, {
  label: 'Practice Workflow',
  icon: ActivityIcon
}, {
  label: 'Product Training',
  icon: GraduationCapIcon
}, {
  label: 'Implementation Plan',
  icon: RouteIcon
}];
const highlights = ['Product-fit conversation before you invest', 'Training and implementation guidance', 'Marketing and warranty support considerations', 'A clear path for your team'];
function PartnerMark({
  brand
}: {
  brand: (typeof archivePartnerBrands)[number];
}) {
  if (brand.src) {
    return <img src={brand.src} alt={`${brand.name} logo`} className="manufacturer-strip__logo block h-auto max-h-8 w-full max-w-[124px] object-contain" data-id="element-163" data-mp="2zpds6n3shxb2" />;
  }
  switch (brand.wordmark) {
    case 'aspen':
      return <span className="flex w-full max-w-full flex-col items-center justify-center px-2 text-center leading-none" data-id="element-164" data-mp="3swazmzn6tvh1">
          <span className="text-[14px] font-extrabold tracking-[0.02em] text-teal" data-id="element-165" data-mp="1r2l6z8gwazt">
            ASPEN
          </span>
          <span className="mt-1 text-[10px] font-semibold tracking-[0.08em] text-navy/75" data-id="element-166" data-mp="3o4jb0unldztj">
            LASER
          </span>
        </span>;
    case 'multi-radiance':
      return <span className="flex items-center gap-1.5 whitespace-nowrap text-navy" data-id="element-167" data-mp="3f44jhdk1bohl">
          <span className="flex h-4 w-4 items-center justify-center rounded-full border border-teal/60" data-id="element-168" data-mp="3f1rvainoohw3">
            <span className="h-1.5 w-1.5 rounded-full bg-teal" data-id="element-169" data-mp="30983uf9wbyuh" />
          </span>
          <span className="flex flex-col leading-none" data-id="element-170" data-mp="35819jlixylk3">
            <span className="text-[12px] font-bold tracking-[-0.04em]" data-id="element-171" data-mp="yf8tpx8ifnjd">
              Multi Radiance
            </span>
            <span className="mt-0.5 text-[7px] font-semibold uppercase tracking-[0.14em] text-teal" data-id="element-172" data-mp="13daceh20xkub">
              Medical
            </span>
          </span>
        </span>;
    case 'lumanova':
      return <span className="flex items-center gap-1.5 whitespace-nowrap text-navy" data-id="element-173" data-mp="wx0hxtor4ucv">
          <span className="h-3 w-3 rounded-full border-[3px] border-teal" data-id="element-174" data-mp="s00e7pfdk3ox" />
          <span className="text-[17px] font-bold tracking-[-0.05em]" data-id="element-175" data-mp="nyf4ue21u7jh">
            LumaNova
          </span>
        </span>;
    default:
      return <span className="max-w-full px-1 text-center text-[12px] font-bold leading-tight tracking-[-0.02em] text-navy sm:text-[13px]" data-id="element-176" data-mp="1kpqpzrtjathv">
          {brand.name}
        </span>;
  }
}
export function ArchiveIntro() {
  return <>
      <section className="manufacturer-strip w-full max-w-full overflow-x-hidden border-y border-slate-200 bg-slate-50/90" data-id="element-177" data-mp="16xawbhmiw7tm">
        <div className="manufacturer-strip__inner mx-auto w-full max-w-7xl box-border overflow-x-hidden px-5 py-7 sm:px-8" data-id="element-178" data-mp="23f72ldihucen">
          <p className="manufacturer-strip__heading mx-auto max-w-full whitespace-normal break-words px-2 text-center text-[11px] font-semibold uppercase leading-snug tracking-[0.16em] text-charcoal/55" data-id="element-179" data-mp="1ylkrvqvw4ilh">
            <span className="manufacturer-strip__heading-mobile" data-id="element-180" data-mp="3mbd01p57tkul">
              Our Partners
            </span>
            <span className="manufacturer-strip__heading-desktop" data-id="element-181" data-mp="3va6tj6n91v3x">
              Our Partners
            </span>
          </p>
          <ul className="manufacturer-strip__chips mx-auto mt-4 grid w-full max-w-[300px] grid-cols-1 gap-2.5 sm:max-w-7xl sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9 lg:gap-2" aria-label="Trusted modality brands" data-id="element-182" data-mp="44ovc0e2uzpg">
            {archivePartnerBrands.map(brand => <li key={brand.name} className={`flex h-14 min-w-0 w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-3 shadow-[0_3px_10px_-7px_rgba(26,27,75,0.34)] ${brand.wordmark === 'aspen' ? 'overflow-visible' : 'overflow-hidden'}`} data-id="element-183" data-mp="hvjx6sq5sgjj">
                <PartnerMark brand={brand} data-id="element-184" data-mp="e5g2olgx12b2" />
              </li>)}
          </ul>
        </div>
      </section>

      <section className="before-you-buy w-full overflow-hidden bg-white px-4 py-20 sm:px-0 sm:py-24" data-id="element-185" data-mp="97eanb1ihsng">
        <div className="before-you-buy__inner mx-auto grid w-full max-w-[358px] items-center gap-10 sm:max-w-7xl sm:px-8 lg:grid-cols-[1.05fr_0.95fr]" data-id="element-186" data-mp="nyn6i1w0ca91">
          <div className="before-you-buy__copy w-full min-w-0" data-id="element-187" data-mp="3rchk17yigqto">
            <h2 className="before-you-buy__heading w-full max-w-full whitespace-normal break-words font-display text-[30px] font-bold leading-[1.08] tracking-tight text-navy sm:max-w-xl sm:text-[2.4rem] sm:leading-tight" data-id="element-188" data-mp="3me7miqpbde4w">
              The Right Equipment = Better Outcomes
            </h2>
            <p className="before-you-buy__copy-text mt-5 w-full max-w-full text-[16px] leading-relaxed text-charcoal/75 sm:max-w-xl" data-id="element-189" data-mp="9cep9wmxq71">
              A quick conversation can help a practice avoid investing in
              technology that does not match its patients, workflow, budget, or
              training capacity. JAG Medical Solutions helps doctors compare
              modalities, understand implementation needs, and choose equipment
              with a clear plan for adoption.
            </p>
            <a href={ARCHIVE_CONTACT.phoneHref} className="before-you-buy__cta mt-8 inline-flex max-w-full items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-navy/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2" data-id="element-190" data-mp="1e6kxo87p9y5n">
              <PhoneIcon className="h-[18px] w-[18px] shrink-0" data-id="element-191" data-mp="1t3lh7ofqmmt1" />
              Book a Call
            </a>
          </div>

          <aside className="before-you-buy__card mx-auto w-full min-w-0 rounded-3xl border border-slate-200 bg-white shadow-[0_28px_65px_-30px_rgba(26,27,75,0.38)] sm:max-w-none" data-id="element-192" data-mp="2kv1b83jmjbta">
            <div className="before-you-buy__card-body p-5 sm:p-6" data-id="element-193" data-mp="2pnuf6p79qg4b">
              <div className="h-1 w-10 rounded-full bg-teal" aria-hidden="true" data-id="element-194" data-mp="2zgf5s28p2b6z" />
              <h3 className="mt-5 font-display text-xl font-bold text-navy" data-id="element-195" data-mp="2ve0mjh1459f6">
                The JAG Medical Solutions Standard
              </h3>
              <p className="mt-2 max-w-full text-sm leading-relaxed text-charcoal/65 sm:max-w-md" data-id="element-196" data-mp="2ligvp91ep0xa">
                Below is our criteria used to evaluate any equipment before
                it&apos;s implemented into your practice.
              </p>

              <div className="before-you-buy__factors mt-5 grid gap-2.5 sm:grid-cols-2" data-id="element-197" data-mp="2bq1b03yq5y8p">
                {REVIEW_FACTORS.map(({
                label,
                icon: Icon
              }) => <div key={label} className="before-you-buy__factor group min-w-0 rounded-xl border border-slate-100 bg-slate-50 px-3.5 py-3 transition-[transform,border-color,background-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-teal/40 hover:bg-teal/[0.04] hover:shadow-sm" data-id="element-198" data-mp="22s8wv34qowmw">
                    <div className="flex items-center gap-2.5" data-id="element-199" data-mp="1nofujq4awah1">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-teal/10 text-teal transition-colors duration-200 group-hover:bg-teal group-hover:text-white" data-id="element-200" data-mp="1nmv1y61jr1lt">
                        <Icon className="h-3.5 w-3.5" data-id="element-201" data-mp="1xm3r8llgvs1u" />
                      </span>
                      <span className="min-w-0 text-[12px] font-semibold leading-snug text-navy" data-id="element-202" data-mp="1006c8y6aq4un">
                        {label}
                      </span>
                    </div>
                    <div className="mt-3 h-px w-8 bg-teal/60 transition-[width] duration-200 group-hover:w-full" data-id="element-203" data-mp="19wrg3g6kx9ww" />
                  </div>)}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section id="product-of-the-month" className="w-full border-y border-slate-200/80 bg-slate-100 py-20 sm:py-24" data-id="element-204" data-mp="h83ufjhx4iit">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8" data-id="element-205" data-mp="20dngvxlwcoz1">
          <header className="mx-auto w-full max-w-[358px] text-center sm:max-w-2xl" data-id="element-206" data-mp="20fztrwcss958">
            <h2 className="font-display text-[30px] font-extrabold leading-[1.08] tracking-tight text-navy sm:text-[2.6rem]" data-id="element-207" data-mp="1we4tmv6ls1t1">
              <span className="block sm:inline" data-id="element-208" data-mp="25czz251tp7va">Product of the</span>{' '}
              <span className="block sm:inline" data-id="element-209" data-mp="25bowu9o1086a">Month</span>
            </h2>
          </header>

          <div className="mx-auto mt-10 w-full max-w-[358px] overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_24px_65px_-34px_rgba(26,27,75,0.38)] sm:mt-12 sm:max-w-none sm:p-8 lg:p-10" data-id="element-210" data-mp="2jayc974eqy97">
            <div className="grid w-full min-w-0 items-center gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:gap-14" data-id="element-211" data-mp="2t4ouvvdusegz">
              <div className="order-2 min-w-0 lg:order-1" data-id="element-212" data-mp="2o5u68ct7fvoy">
                <h3 className="font-display text-3xl font-bold leading-[1.12] tracking-tight text-navy sm:text-[2.35rem]" data-id="element-213" data-mp="331jrg8qkg4sm">
                  AWUSA Dual-Radial Shockwave
                </h3>
                <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-charcoal/80" data-id="element-214" data-mp="3hoigxy5v4p4p">
                  A focused option for practices evaluating radial shockwave
                  protocols. JAG helps you consider clinical fit, staff
                  readiness, marketing support, warranty considerations, and
                  implementation before you invest.
                </p>
                <ul className="mt-8 grid gap-x-7 gap-y-3.5 sm:grid-cols-2" data-id="element-215" data-mp="2xz8730sssyei">
                  {highlights.map(highlight => <li key={highlight} className="flex items-start gap-2.5" data-id="element-216" data-mp="32rhpzwvhwy7a">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal/15 text-teal" data-id="element-217" data-mp="2p036loxoeuzp">
                        <CheckIcon className="h-3.5 w-3.5" data-id="element-218" data-mp="2tvsb86ojz22v" />
                      </span>
                      <span className="text-[14px] font-medium leading-snug text-charcoal" data-id="element-219" data-mp="2f1dxolnrckvl">
                        {highlight}
                      </span>
                    </li>)}
                </ul>
                <a href={ARCHIVE_CONTACT.phoneHref} className="mt-9 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-navy/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2" data-id="element-220" data-mp="3huo68jngl7m9">
                  <PhoneIcon className="h-[18px] w-[18px]" data-id="element-221" data-mp="4eq6i70u4nba" />
                  Call About This Product
                </a>
              </div>

              <div className="order-1 min-w-0 lg:order-2" data-id="element-222" data-mp="3qszu4dwxxrb0">
                <div className="relative overflow-hidden rounded-2xl border border-navy/10 bg-navy shadow-[0_24px_52px_-26px_rgba(26,27,75,0.52)]" data-id="element-223" data-mp="3qsgnvugnkrct">
                  <img src={ARCHIVE_SHOCKWAVE_IMAGE} alt="AWUSA Dual-Radial Shockwave system" className="aspect-[4/3] w-full object-cover" data-id="element-224" data-mp="ncjazdr3x5as" />
                  <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/15" data-id="element-225" data-mp="2csmyruy9bhan" />
                  <div className="pointer-events-none absolute bottom-0 left-0 h-1 w-24 bg-teal" data-id="element-226" data-mp="27v3i6oe36sfj" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}